/**
 * Which suggestions on the Proposed tab already have a test, and which
 * ones a run is writing right now (#2260).
 *
 * **Why this exists at all.** `CandidatesSection` has always taken the
 * per-variant status as a prop. `Applications.tsx` used to fetch it and
 * pass it down; the #1827 move to the Tests area brought the component
 * across and left the fetch behind. The prop is optional, so nothing
 * failed to compile and nothing errored — it simply became `undefined`
 * forever, and with it the status badges, the per-status summary, the
 * Status filter, and the guard against building the same suggestion
 * twice. This hook is that fetch, restored, and kept in one place so the
 * next move takes it along.
 *
 * **Why it also tracks runs.** "Create test" starts a generation run that
 * takes minutes, and the linked test case only exists once the run's
 * planner writes it. So during the run the suggestion genuinely has no
 * test and the status correctly says so — which would leave the operator
 * free to start a second run for the same suggestion. Only the page knows
 * a run is in flight, so the page holds that and hands it to the section.
 *
 * **Why runs are a map, not one run.** An operator can tick some
 * suggestions, start a run, then tick others and start another while the
 * first is still writing. Tracking a single run would release the first
 * run's suggestions the moment the second started, reopening the exact
 * window this closes. Each run is followed and released on its own.
 *
 * **Why polling rather than the live-run socket.** `useLiveRun` is the
 * Results page's machinery, built to stream progress into a run view.
 * Borrowing it to refresh a set of badges is far more surface than the
 * problem needs. This poll is bounded three ways and stops itself.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { applicationsAPI, testRunsAPI } from '@/lib/api'
import type { CandidateScriptedStatusResponse } from '@/types'

/** How often to re-read while a run is writing. Long enough not to
 * hammer the endpoint over a run measured in minutes, short enough that
 * the badges land while the operator is still looking at the tab. */
const POLL_MS = 10_000

/** Stop after this many polls even if no run reports finishing. A run
 * that dies without a terminal status would otherwise be followed for as
 * long as the tab stays open. At `POLL_MS` this is about ten minutes,
 * comfortably past the "few minutes" the confirm dialog promises. */
const MAX_POLLS = 60

/** Run states that mean nothing further will be written. */
const TERMINAL = new Set(['completed', 'failed', 'cancelled'])

export interface ProposedScriptedStatus {
  /** The status for the selected variant, or `undefined` when it is not
   * known — still loading, or the read failed. The section treats
   * `undefined` as "do not offer the action", so the two are deliberately
   * not distinguished: neither justifies letting an operator create a
   * possible duplicate. */
  scriptedStatus: CandidateScriptedStatusResponse | undefined
  /** Every suggestion any in-flight run was started for. */
  writingCandidateIds: ReadonlySet<string>
  /** Call when the section starts a run, to begin following it. */
  onRunStarted: (runId: string, candidateIds: string[]) => void
  /** Call when something changed the suggestions themselves. */
  refresh: () => void
}

export interface ProposedScriptedStatusOptions {
  /** Called whenever the status is read again after the first read: an
   * explicit refresh, or a poll while a run is writing. The page uses it
   * to re-read coverage too, so the tab's coverage bar moves with the
   * row badges. */
  onReread?: () => void
  /** Called once a run started here has ended. The run may have changed
   * the suggestions themselves (a test it could not write is sent back
   * with a reason, an earlier reason is cleared), which the status does
   * not carry, so the page reloads the list. A run is followed to its end
   * even after its rows unblock, because a test case appearing does not
   * mean the writer is done with it. */
  onRunFinished?: () => void
}

export function useProposedScriptedStatus(
  appId: string | null | undefined,
  variantId: string | null | undefined,
  options: ProposedScriptedStatusOptions = {},
): ProposedScriptedStatus {
  const [scriptedStatus, setScriptedStatus] = useState<CandidateScriptedStatusResponse | undefined>()
  /** runId → the suggestions that run was started for. */
  const [inFlight, setInFlight] = useState<ReadonlyMap<string, ReadonlySet<string>>>(new Map())
  const [refreshKey, setRefreshKey] = useState(0)
  const pollsRef = useRef(0)

  // Held in a ref so a new callback each render does not restart the
  // read or the poll.
  const onRereadRef = useRef(options.onReread)
  onRereadRef.current = options.onReread
  const onRunFinishedRef = useRef(options.onRunFinished)
  onRunFinishedRef.current = options.onRunFinished
  /** Runs started here that have not ended yet, whether or not their rows
   * are still blocked. */
  const [following, setFollowing] = useState<ReadonlySet<string>>(new Set())

  const refresh = useCallback(() => {
    setRefreshKey(k => k + 1)
    onRereadRef.current?.()
  }, [])

  const onRunStarted = useCallback((runId: string, candidateIds: string[]) => {
    setFollowing(prev => new Set(prev).add(runId))
    setInFlight(prev => {
      const next = new Map(prev)
      next.set(runId, new Set(candidateIds))
      return next
    })
  }, [])

  const writingCandidateIds = useMemo(() => {
    const out = new Set<string>()
    for (const ids of inFlight.values()) for (const id of ids) out.add(id)
    return out
  }, [inFlight])

  // The read itself. Re-fires on app, variant, or an explicit refresh.
  // A failure clears the status rather than leaving a stale one: the
  // section blocks on an unknown status, and blocking is the safe
  // outcome when we could not check.
  useEffect(() => {
    if (!appId || !variantId) {
      setScriptedStatus(undefined)
      return
    }
    let cancelled = false
    void applicationsAPI
      .scriptedStatus(appId, variantId)
      .then(res => { if (!cancelled) setScriptedStatus(res) })
      .catch(() => { if (!cancelled) setScriptedStatus(undefined) })
    return () => { cancelled = true }
  }, [appId, variantId, refreshKey])

  // A run belongs to the variant it was started in. Dropping the in-flight
  // map on a variant change stops rows in a DIFFERENT variant being
  // blocked by a run that has nothing to do with them.
  useEffect(() => {
    setInFlight(new Map())
    setFollowing(new Set())
    pollsRef.current = 0
  }, [appId, variantId])

  // Follow every in-flight run, refreshing the status as they go. A run
  // is released on the first of: all of its suggestions showing a linked
  // test, the run reaching a terminal state, or the attempt cap.
  useEffect(() => {
    if (inFlight.size === 0 && following.size === 0) { pollsRef.current = 0; return }
    if (!appId || !variantId) return

    let cancelled = false
    const timer = window.setInterval(() => {
      pollsRef.current += 1
      if (pollsRef.current > MAX_POLLS) {
        if (!cancelled) {
          setInFlight(new Map())
          setFollowing(new Set())
        }
        return
      }
      void (async () => {
        // Whether THIS cycle got a fresh status decides whether releasing
        // a run is safe. Releasing on a stale snapshot would re-enable
        // rows the snapshot still calls unbuilt, which is the window this
        // hook exists to close.
        let fresh: CandidateScriptedStatusResponse | undefined
        try {
          fresh = await applicationsAPI.scriptedStatus(appId, variantId)
          if (cancelled) return
          setScriptedStatus(fresh)
          onRereadRef.current?.()
        } catch {
          // Leave the status as it is for now; the release path below
          // handles the risk that it is stale.
        }

        // Runs whose rows are already unblocked: only whether they ended.
        const ended: string[] = []
        for (const runId of following) {
          if (inFlight.has(runId)) continue
          try {
            const run = await testRunsAPI.getById(runId)
            if (cancelled) return
            if (run && TERMINAL.has(run.status)) ended.push(runId)
          } catch {
            // Unreadable run: keep following it until the cap.
          }
        }

        const done: string[] = []
        let releasedOnStaleStatus = false

        for (const [runId, ids] of inFlight) {
          if (fresh && [...ids].every(id => (fresh!.byCandidate[id] ?? 'proposed') !== 'proposed')) {
            done.push(runId)
            continue
          }
          try {
            const run = await testRunsAPI.getById(runId)
            if (cancelled) return
            if (run && TERMINAL.has(run.status)) {
              // Terminal without every suggestion written means the run
              // ended early or partly failed. Release anyway: trying
              // again has to be possible.
              done.push(runId)
              ended.push(runId)
              if (!fresh) releasedOnStaleStatus = true
            }
          } catch {
            // Unreadable run: keep following it until the cap.
          }
        }

        if (cancelled) return
        if (ended.length > 0) {
          setFollowing(prev => {
            const next = new Set(prev)
            for (const runId of ended) next.delete(runId)
            return next
          })
          onRunFinishedRef.current?.()
        }
        if (done.length === 0) return
        setInFlight(prev => {
          const next = new Map(prev)
          for (const runId of done) next.delete(runId)
          return next
        })
        if (releasedOnStaleStatus) {
          // Rows are about to become selectable again on the strength of
          // a status we could not re-read. Say we do not know and go and
          // find out, so the section keeps blocking until it is sure.
          setScriptedStatus(undefined)
          refresh()
        }
      })()
    }, POLL_MS)

    return () => { cancelled = true; window.clearInterval(timer) }
  }, [appId, variantId, inFlight, following, refresh])

  return { scriptedStatus, writingCandidateIds, onRunStarted, refresh }
}
